import React, { FC } from 'react';
import { Col, Row, Space, Steps, Spin, Typography } from 'antd';
import { Redirect, RouteComponentProps } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import cx from 'classnames';
import styled from 'styled-components';

import { PageHeader } from '~/layout';
import { useKycCheck, useKycPage } from '~/hooks/kyc';
import PrimaryDocument from './steps/PrimaryDocument';
import AdditionalDocuments from './steps/AdditionalDocuments';
import PayoutDetails from './steps/PayoutDetails';
import CompanyDetails from './steps/CompanyDetails';
import CompanyAddress from './steps/CompanyAddress';
import KycContent from './KycContent';
import { useDirection } from '~/hooks';
import theme from '~/theme';
import featuresWithDefaults from '~/featuresWithDefaults';
import ApplicationsScreen from './applications/ApplicationsScreen';
import useIsApplicationsAvailable from '~/hooks/useIsApplicationsAvailable';

interface Props extends RouteComponentProps {
  className?: string;
}

const KycPage: FC<Props> = (props) => {
  const { className } = props;
  const [t] = useTranslation('kyc');
  const kycCheck = useKycCheck();
  const kycPage = useKycPage();
  const { currentPageNumber } = kycPage.state;
  const [dir] = useDirection();
  const hasApplicationFeature = useIsApplicationsAvailable();
  if (hasApplicationFeature && featuresWithDefaults?.kyc?.application?.type)
    return <ApplicationsScreen />;
  if (!kycCheck.ready) return <Spin />;
  if (kycCheck.isPending) return <KycContent {...kycCheck} thanks />;
  return (
    <>
      {kycCheck.isCompleted && <Redirect to="/" />}
      <div className={cx(className, 'KycPage')}>
        <Row gutter={gutter}>
          <Col span={24}>
            <PageHeader heading={t('Company profile details')} showSearch={false} />
          </Col>
        </Row>
        <KycContainer size={80} direction="vertical" dir={dir}>
          <Steps current={currentPageNumber} labelPlacement="vertical">
            <Steps.Step
              title={<StepTitle>{t('Company details')}</StepTitle>}
              description={t('Basic Information about your company')}
            />
            <Steps.Step
              title={<StepTitle>{t('Company address')}</StepTitle>}
              description={t(
                'This must be the location where you take payments and where we can reach you'
              )}
            />
            <Steps.Step
              title={<StepTitle>{t('Primary company documents')}</StepTitle>}
              description={t('Incorporation certificate or business license scan')}
            />
            <Steps.Step
              title={<StepTitle>{t('Additional company documents')}</StepTitle>}
              description={t('Information about company owners, extra documents')}
            />
            <Steps.Step
              title={<StepTitle>{t('Payout details')}</StepTitle>}
              description={t('Bank account details for payout')}
            />
          </Steps>
          {currentPageNumber === 0 ? (
            <CompanyDetails />
          ) : currentPageNumber === 1 ? (
            <CompanyAddress />
          ) : currentPageNumber === 2 ? (
            <PrimaryDocument />
          ) : currentPageNumber === 3 ? (
            <AdditionalDocuments />
          ) : currentPageNumber === 4 ? (
            <PayoutDetails />
          ) : null}
        </KycContainer>
      </div>
    </>
  );
};

const StepTitle: FC<React.ComponentProps<typeof Typography.Title>> = (props) => {
  return <Typography.Title {...props} className={cx(props.className, 'Step-Title')} level={5} />;
};

const gutter: [number, number] = [16, 24];

const KycContainer = styled(Space)`
  width: 100%;
  max-width: 1200px;

  .ant-steps-label-vertical .ant-steps-item-icon {
    margin-left: unset;
  }

  .ant-steps-label-vertical .ant-steps-item-tail {
    margin-left: ${({ dir }) => (dir === 'ltr' ? '16px' : '0px')};
    margin-right: ${({ dir }) => (dir === 'rtl' ? '16px' : '0px')};
  }

  .ant-steps-label-vertical .ant-steps-item-content {
    width: unset;
    text-align: unset;
    margin-right: 20px;
  }

  .ant-steps-item:last-child {
    flex: 1 1;
  }

  .Step-Title {
    font-weight: 400;
    line-height: 21px;
    min-height: 45px;
    margin-bottom: 0;
    @media (max-width: ${theme.breakpoints.sm}px) {
      min-height: unset;
    }
  }

  .Step-Container {
    width: 100%;
  }
`;

export default styled(KycPage)`
  padding-bottom: 24px;
`;
